import { Link, useParams } from "react-router-dom";
import { MapPin, Clock, GraduationCap, Briefcase, Stethoscope, Check, CalendarCheck } from "lucide-react";
import Seo from "@/components/Seo";
import { PageHeader } from "@/components/PageHeader";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { AppointmentCTA } from "@/components/AppointmentCTA";
import { DoctorCard } from "@/components/DoctorCard";
import { SITE } from "@/config/site";
import { getDoctorBySlug, DOCTORS } from "@/data/doctors";
import { WA_MESSAGES } from "@/utils/whatsapp";

export default function DoctorDetail() {
  const { slug } = useParams();
  const doctor = getDoctorBySlug(slug);

  if (!doctor) {
    return (
      <section className="py-24 text-center" data-testid="doctor-not-found">
        <h1 className="font-heading text-3xl font-bold text-navy-900">Doctor not found</h1>
        <p className="mt-3 text-slate-600">The profile you are looking for does not exist.</p>
        <Link to="/doctors" className="mt-6 inline-flex rounded-full bg-navy-800 px-6 py-3 text-sm font-semibold text-white" data-testid="doctor-not-found-back">
          View all doctors
        </Link>
      </section>
    );
  }

  const others = DOCTORS.filter((d) => d.slug !== slug).slice(0, 4);

  return (
    <>
      <Seo
        title={`${doctor.name}, ${doctor.specialty} — ${SITE.name}`}
        siteName={SITE.name}
        description={`${doctor.name} (${doctor.qualifications}), ${doctor.designation} at ${SITE.name}, Kasdol. View consultation schedule and book on WhatsApp.`}
        image={doctor.image}
      />
      <PageHeader
        title={doctor.name}
        description={`${doctor.qualifications} · ${doctor.designation} · ${doctor.specialty}`}
        crumbs={[{ label: "Doctors", to: "/doctors" }, { label: doctor.name }]}
      />

      <section className="py-14 sm:py-20" data-testid="doctor-profile-section">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 sm:px-6 lg:grid-cols-3 lg:px-8">
          {/* Sidebar card */}
          <aside className="lg:sticky lg:top-32 lg:self-start">
            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <div className="relative">
                <img src={doctor.image} alt={`Portrait of ${doctor.name}`} className="aspect-[4/3] w-full object-cover" data-testid="doctor-profile-image" />
                {doctor.isDemo && (
                  <span className="absolute left-3 top-3 rounded-full bg-navy-900/85 px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-white">
                    Sample Profile
                  </span>
                )}
              </div>
              <div className="space-y-4 p-6">
                <div className="flex items-start gap-3 text-sm text-slate-600">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
                  <span>{doctor.location}</span>
                </div>
                <WhatsAppButton message={WA_MESSAGES.doctor(doctor.name)} className="w-full" testId="doctor-whatsapp-appointment-button">
                  Book Appointment on WhatsApp
                </WhatsAppButton>
                <Link
                  to="/appointments"
                  className="flex w-full items-center justify-center gap-2 rounded-full border-2 border-navy-200 px-6 py-3 text-sm font-semibold text-navy-800 transition-colors hover:border-navy-800 hover:bg-navy-50"
                  data-testid="doctor-appointment-form-link"
                >
                  <CalendarCheck className="h-4 w-4" aria-hidden="true" /> Use Appointment Form
                </Link>
              </div>
            </div>
          </aside>

          {/* Details */}
          <div className="space-y-10 lg:col-span-2">
            <div>
              <h2 className="font-heading text-2xl font-bold text-navy-900">About {doctor.name}</h2>
              <p className="mt-4 leading-relaxed text-slate-600">{doctor.intro}</p>
            </div>

            <div className="grid gap-6 sm:grid-cols-3">
              {[
                { icon: GraduationCap, label: "Qualifications", value: doctor.qualifications },
                { icon: Briefcase, label: "Experience", value: doctor.experience },
                { icon: Stethoscope, label: "Specialization", value: doctor.specialty },
              ].map(({ icon: I, label, value }) => (
                <div key={label} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`doctor-fact-${label.toLowerCase()}`}>
                  <I className="h-5 w-5 text-emerald-600" aria-hidden="true" />
                  <p className="mt-3 text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</p>
                  <p className="mt-1 text-sm font-medium text-navy-900">{value}</p>
                </div>
              ))}
            </div>

            <div>
              <h2 className="font-heading text-2xl font-bold text-navy-900">Areas of Expertise</h2>
              <ul className="mt-5 grid gap-3 sm:grid-cols-2">
                {doctor.expertise.map((e) => (
                  <li key={e} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" /> {e}
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h2 className="flex items-center gap-2 font-heading text-2xl font-bold text-navy-900">
                <Clock className="h-5 w-5 text-emerald-600" aria-hidden="true" /> Clinic Consultation Schedule
              </h2>
              <div className="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white" data-testid="doctor-schedule-table">
                {doctor.schedule.map((s, i) => (
                  <div key={s.day} className={`flex items-center justify-between px-5 py-4 ${i % 2 ? "bg-slate-50" : "bg-white"}`}>
                    <span className="text-sm font-semibold text-navy-900">{s.day}</span>
                    <span className="text-sm text-slate-600">{s.time}</span>
                  </div>
                ))}
              </div>
              <p className="mt-3 text-xs text-slate-500">
                Demo schedule — please call {SITE.phoneDisplay} to confirm the doctor's actual timings before visiting.
              </p>
            </div>
          </div>
        </div>
      </section>

      {others.length > 0 && (
        <section className="bg-white py-16" data-testid="doctor-related-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <h2 className="mb-8 text-center font-heading text-2xl font-bold text-navy-900">Other Doctors</h2>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
              {others.map((d) => <DoctorCard key={d.slug} doctor={d} />)}
            </div>
          </div>
        </section>
      )}

      <AppointmentCTA />
    </>
  );
}
